import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  CreditCard,
  Plus,
  Edit,
  Clock,
  DollarSign,
  RefreshCw,
  CheckCircle,
  Pause,
  XCircle,
  Loader2,
  Users,
  Search,
  Calendar,
  TrendingUp,
} from "lucide-react";

const AdminSubscriptions = () => {
  const { token, API } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subsLoading, setSubsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    monthly_hours: 40,
    hourly_rate: 150,
    hours_rollover: false,
    max_rollover_hours: 20,
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchSubscriptions(selectedClient.client_id);
    }
  }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
        if (data.length > 0) {
          setSelectedClient(data[0]);
        }
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async (clientId) => {
    setSubsLoading(true);
    try {
      const response = await fetch(`${API}/admin/clients/${clientId}/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setSubsLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!selectedClient) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        monthly_hours: formData.monthly_hours.toString(),
        hourly_rate: formData.hourly_rate.toString(),
        hours_rollover: formData.hours_rollover.toString(),
      });
      
      if (formData.hours_rollover && formData.max_rollover_hours) {
        params.append("max_rollover_hours", formData.max_rollover_hours.toString());
      }
      
      const response = await fetch(
        `${API}/admin/clients/${selectedClient.client_id}/subscription?${params.toString()}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Subscription created");
        fetchSubscriptions(selectedClient.client_id);
        setCreateModalOpen(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create subscription");
      }
    } catch (error) {
      toast.error("Failed to create subscription");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSubscription = async () => {
    if (!selectedSubscription) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        monthly_hours: formData.monthly_hours.toString(),
        hourly_rate: formData.hourly_rate.toString(),
        hours_rollover: formData.hours_rollover.toString(),
      });
      
      if (formData.hours_rollover && formData.max_rollover_hours) {
        params.append("max_rollover_hours", formData.max_rollover_hours.toString());
      }
      
      const response = await fetch(
        `${API}/admin/subscriptions/${selectedSubscription.subscription_id}?${params.toString()}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        toast.success("Subscription updated");
        fetchSubscriptions(selectedClient.client_id);
        setEditModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update subscription");
      }
    } catch (error) {
      toast.error("Failed to update subscription");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (subscription, newStatus) => {
    try {
      const params = new URLSearchParams({ status: newStatus });
      const response = await fetch(
        `${API}/admin/subscriptions/${subscription.subscription_id}?${params.toString()}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        toast.success(`Subscription ${newStatus}`);
        fetchSubscriptions(selectedClient.client_id);
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openEditModal = (subscription) => {
    setSelectedSubscription(subscription);
    setFormData({
      monthly_hours: subscription.monthly_hours,
      hourly_rate: subscription.hourly_rate,
      hours_rollover: subscription.hours_rollover || false,
      max_rollover_hours: subscription.max_rollover_hours || 20,
    });
    setEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      monthly_hours: 40,
      hourly_rate: 150,
      hours_rollover: false,
      max_rollover_hours: 20,
    });
  };

  const filteredClients = clients.filter((client) => {
    const search = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.company_name?.toLowerCase().includes(search)
    );
  });

  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: "outline", icon: Clock, label: "Pending", className: "border-yellow-500 text-yellow-600" },
      active: { variant: "default", icon: CheckCircle, label: "Active", className: "bg-green-600" },
      paused: { variant: "outline", icon: Pause, label: "Paused", className: "border-orange-500 text-orange-600" },
      cancelled: { variant: "destructive", icon: XCircle, label: "Cancelled" },
      cancelling: { variant: "outline", icon: XCircle, label: "Cancelling", className: "border-red-500 text-red-600" },
    };
    const { variant, icon: Icon, label, className } = config[status] || config.pending;
    return (
      <Badge variant={variant} className={className}>
        <Icon className="h-3 w-3 mr-1" />
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-rnb-navy mb-2">
              Subscription Management
            </h1>
            <p className="text-rnb-gray">
              Create and manage client retainer subscriptions with Stripe.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Client List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Clients
                </CardTitle>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-rnb-gray" />
                  <Input
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-rnb-primary" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredClients.map((client) => (
                      <div
                        key={client.client_id}
                        onClick={() => setSelectedClient(client)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedClient?.client_id === client.client_id
                            ? "bg-rnb-primary/10 border-2 border-rnb-primary"
                            : "bg-slate-50 hover:bg-slate-100 border-2 border-transparent"
                        }`}
                      >
                        <p className="font-medium text-rnb-navy">{client.name || client.user?.name}</p>
                        <p className="text-sm text-rnb-gray">{client.company_name || client.email}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Subscriptions List */}
          <div className="lg:col-span-2">
            {selectedClient ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-rnb-navy">
                      {selectedClient.name || selectedClient.user?.name}'s Subscriptions
                    </h2>
                    <p className="text-sm text-rnb-gray">{selectedClient.company_name}</p>
                  </div>
                  <Button onClick={() => { resetForm(); setCreateModalOpen(true); }} data-testid="create-subscription-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Subscription
                  </Button>
                </div>

                {subsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-rnb-primary" />
                  </div>
                ) : subscriptions.length > 0 ? (
                  <div className="space-y-4">
                    {subscriptions.map((sub) => {
                      const hoursPercent = sub.monthly_hours > 0 
                        ? Math.min(100, (sub.hours_used_this_cycle / sub.monthly_hours) * 100) 
                        : 0;
                      
                      return (
                        <Card key={sub.subscription_id} data-testid={`subscription-${sub.subscription_id}`}>
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                  <CreditCard className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-rnb-navy">
                                    ${sub.monthly_price?.toLocaleString()}/month Retainer
                                  </h3>
                                  <p className="text-sm text-rnb-gray">
                                    {sub.monthly_hours} hours @ ${sub.hourly_rate}/hr
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(sub.status)}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditModal(sub)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-4 gap-4 mb-4">
                              <div className="text-center p-2 bg-slate-50 rounded-lg">
                                <Clock className="h-4 w-4 text-rnb-gray mx-auto mb-1" />
                                <p className="text-lg font-semibold text-rnb-navy">
                                  {sub.hours_used_this_cycle?.toFixed(1) || 0}
                                </p>
                                <p className="text-xs text-rnb-gray">Used This Cycle</p>
                              </div>
                              <div className="text-center p-2 bg-slate-50 rounded-lg">
                                <TrendingUp className="h-4 w-4 text-rnb-gray mx-auto mb-1" />
                                <p className="text-lg font-semibold text-rnb-navy">
                                  {sub.hours_available?.toFixed(1) || sub.monthly_hours}
                                </p>
                                <p className="text-xs text-rnb-gray">Available</p>
                              </div>
                              <div className="text-center p-2 bg-slate-50 rounded-lg">
                                <RefreshCw className="h-4 w-4 text-rnb-gray mx-auto mb-1" />
                                <p className="text-lg font-semibold text-rnb-navy">
                                  {sub.rollover_balance || 0}
                                </p>
                                <p className="text-xs text-rnb-gray">Rollover Hrs</p>
                              </div>
                              <div className="text-center p-2 bg-slate-50 rounded-lg">
                                <Calendar className="h-4 w-4 text-rnb-gray mx-auto mb-1" />
                                <p className="text-sm font-medium text-rnb-navy">
                                  {formatDate(sub.billing_cycle_start)}
                                </p>
                                <p className="text-xs text-rnb-gray">Cycle Start</p>
                              </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="space-y-1 mb-4">
                              <div className="flex justify-between text-sm">
                                <span className="text-rnb-gray">Hours This Cycle</span>
                                <span className="font-medium text-rnb-navy">
                                  {hoursPercent.toFixed(0)}% used
                                </span>
                              </div>
                              <Progress value={hoursPercent} className="h-2" />
                            </div>

                            {/* Rollover Info */}
                            {sub.hours_rollover && (
                              <div className="flex items-center gap-2 text-sm text-rnb-gray mb-4 p-2 bg-blue-50 rounded">
                                <RefreshCw className="h-4 w-4 text-blue-600" />
                                <span>
                                  Rollover enabled
                                  {sub.max_rollover_hours && ` (max ${sub.max_rollover_hours} hrs)`}
                                </span>
                              </div>
                            )}

                            {/* Quick Actions */}
                            <div className="flex gap-2">
                              {sub.status === "active" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStatusChange(sub, "paused")}
                                >
                                  <Pause className="h-4 w-4 mr-1" />
                                  Pause
                                </Button>
                              )}
                              {sub.status === "paused" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStatusChange(sub, "active")}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Resume
                                </Button>
                              )}
                              {sub.status === "pending" && (
                                <Badge variant="outline" className="border-blue-500 text-blue-600">
                                  Awaiting client payment method
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <CreditCard className="h-12 w-12 text-rnb-gray/30 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-rnb-navy mb-2">No Subscriptions Yet</h3>
                      <p className="text-rnb-gray mb-4">
                        Create a monthly retainer subscription for this client.
                      </p>
                      <Button onClick={() => { resetForm(); setCreateModalOpen(true); }}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Subscription
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Users className="h-12 w-12 text-rnb-gray/30 mx-auto mb-4" />
                  <p className="text-rnb-gray">Select a client to view their subscriptions</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Create Subscription Modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Subscription</DialogTitle>
              <DialogDescription>
                Create a monthly retainer for {selectedClient?.name || selectedClient?.user?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="monthly_hours">Monthly Hours</Label>
                  <Input
                    id="monthly_hours"
                    type="number"
                    value={formData.monthly_hours}
                    onChange={(e) => setFormData({ ...formData, monthly_hours: parseInt(e.target.value) || 0 })}
                    data-testid="monthly-hours-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-rnb-navy">Hours Rollover</p>
                  <p className="text-sm text-rnb-gray">Unused hours carry to next month</p>
                </div>
                <Switch
                  checked={formData.hours_rollover}
                  onCheckedChange={(checked) => setFormData({ ...formData, hours_rollover: checked })}
                />
              </div>

              {formData.hours_rollover && (
                <div className="space-y-2">
                  <Label htmlFor="max_rollover">Max Rollover Hours (optional)</Label>
                  <Input
                    id="max_rollover"
                    type="number"
                    value={formData.max_rollover_hours}
                    onChange={(e) => setFormData({ ...formData, max_rollover_hours: parseInt(e.target.value) || 0 })}
                    placeholder="Leave empty for no cap"
                  />
                </div>
              )}

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-lg font-bold text-green-700">
                  ${(formData.monthly_hours * formData.hourly_rate).toLocaleString()}/month
                </p>
                <p className="text-sm text-green-600">
                  {formData.monthly_hours} hours @ ${formData.hourly_rate}/hour
                </p>
              </div>

              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-700">
                  <strong>Note:</strong> The client will need to add a payment method to activate this subscription.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateSubscription} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Subscription
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Subscription Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Subscription</DialogTitle>
              <DialogDescription>
                Update subscription settings. Changes take effect next billing cycle.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-monthly_hours">Monthly Hours</Label>
                  <Input
                    id="edit-monthly_hours"
                    type="number"
                    value={formData.monthly_hours}
                    onChange={(e) => setFormData({ ...formData, monthly_hours: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-hourly_rate">Hourly Rate ($)</Label>
                  <Input
                    id="edit-hourly_rate"
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-rnb-navy">Hours Rollover</p>
                  <p className="text-sm text-rnb-gray">Unused hours carry to next month</p>
                </div>
                <Switch
                  checked={formData.hours_rollover}
                  onCheckedChange={(checked) => setFormData({ ...formData, hours_rollover: checked })}
                />
              </div>

              {formData.hours_rollover && (
                <div className="space-y-2">
                  <Label htmlFor="edit-max_rollover">Max Rollover Hours</Label>
                  <Input
                    id="edit-max_rollover"
                    type="number"
                    value={formData.max_rollover_hours}
                    onChange={(e) => setFormData({ ...formData, max_rollover_hours: parseInt(e.target.value) || 0 })}
                  />
                </div>
              )}

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-lg font-bold text-green-700">
                  ${(formData.monthly_hours * formData.hourly_rate).toLocaleString()}/month
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateSubscription} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSubscriptions;
